import { inject, Injectable } from '@angular/core';
import { MatBottomSheet } from '@angular/material/bottom-sheet';
import type { Params } from '@angular/router';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { select, Store } from '@ngrx/store';
import {
  catchError,
  concatMap,
  delay,
  EMPTY,
  finalize,
  from,
  last,
  map,
  mergeMap,
  Observable,
  of,
  switchMap,
  tap,
  withLatestFrom,
} from 'rxjs';

import {
  ApiResponse,
  FileService,
  StorageApiUploadInitPayload,
  StorageApiUploadInitResponse,
  UPLOAD_ERROR_EMPTY_FILE,
  UPLOAD_ERROR_USER_CANCELLED,
  UploadService,
} from '@evc/platform';
import { Maybe } from '@evc/web-components';
import { SharedConfigService } from '@shared/services/config/config.service';

import { UploadListComponent } from '@app/components';
import { FileActions, ToastActions, UploadActions } from '@app/store/actions';
import { getRouterParams } from '@app/store/selectors';
import type { BackendError } from '@app/types/error/backend-error.type';
import { UploadErrorEnum } from '@app/types/error/upload-error.enum';

const AUTOCLOSE_DELAY = 2800;
let autoCloseTimeout: Maybe<number>;

export type StorageApiUploadChunkRequest = {
  sasUri: string;
  uploadId: string;
  fileName: string;
  chunkSize: number;
  parentId?: string;
};

@Injectable()
export class UploadEffects {
  private readonly store = inject(Store);
  private readonly bottomSheet = inject(MatBottomSheet);
  private readonly actions$ = inject(Actions);
  private readonly uploadService = inject(UploadService);
  private readonly fileService = inject(FileService);
  private readonly sharedConfigService = inject(SharedConfigService);

  public uploadFiles$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UploadActions.uploadFiles),
      withLatestFrom(this.store.pipe(select(getRouterParams))),
      switchMap(
        ([
          { files, paths },
          params,
        ]: [
          { files: File[]; paths?: string[] },
          Params,
        ]) => {
          const bottomSheetRef =
            this.bottomSheet._openedBottomSheetRef ??
            this.bottomSheet.open(UploadListComponent, {
              hasBackdrop: false,
              closeOnNavigation: false,
              autoFocus: 'first-heading',
              panelClass: 'upload-list',
              disableClose: true,
            });

          if (autoCloseTimeout) clearTimeout(autoCloseTimeout);

          return this.#createFolderTree$(paths ?? [], params.id).pipe(
            tap((folderIds) => {
              if (folderIds.size > 1) {
                this.store.dispatch(FileActions.loadData({ page: 0, search: '' }));
              }
            }),
            switchMap((folderIds) =>
              from(files).pipe(
                concatMap((file, index) => {
                  const fileName = file.name;
                  const tempId = `upload-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;

                  this.store.dispatch(
                    UploadActions.uploadFileStarted({ name: fileName, uploadId: tempId }),
                  );

                  return this.#uploadFile$(
                    {
                      fileName,
                      contentType: this.getMimetype(file),
                      sizeInBytes: file.size,
                      parentId: folderIds.get(paths?.[index] ?? '') ?? params.id,
                    },
                    file,
                  );
                }),
              ),
            ),
            delay(0),
            finalize(() => {
              this.store.dispatch(UploadActions.uploadFilesAllComplete());
              this.uploadService.clearActiveQueue();

              autoCloseTimeout = setTimeout(() => {
                bottomSheetRef.instance.close(true);
              }, AUTOCLOSE_DELAY) as unknown as number;
            }),
            catchError(() => {
              return of(
                UploadActions.uploadFilesFail({
                  error: 'upload.failed.global',
                }),
              );
            }),
          );
        },
      ),
    ),
  );

  #createFolderTree$(paths: string[], rootId: string): Observable<Map<string, string>> {
    const folderIds = new Map<string, string>([['', rootId]]);
    const directories = [...new Set(paths.filter(Boolean))].sort(
      (a, b) => a.split('/').length - b.split('/').length,
    );

    if (!directories.length) return of(folderIds);

    return from(directories).pipe(
      concatMap((path) => {
        const separator = path.lastIndexOf('/');
        const name = path.slice(separator + 1);
        const parentPath = separator === -1 ? '' : path.slice(0, separator);

        return this.fileService
          .createFolder$({ name, parentId: folderIds.get(parentPath) ?? rootId })
          .pipe(tap((folder) => folderIds.set(path, folder.id)));
      }),
      last(),
      map(() => folderIds),
    );
  }

  #uploadFile$(
    request: StorageApiUploadInitPayload,
    file: File,
  ): Observable<ApiResponse<StorageApiUploadInitResponse['result']>> {
    const { fileName } = request;

    return this.uploadService.initUpload$(request).pipe(
      switchMap((initResponse) => {
        const { uploadId, chunkSize } = initResponse;
        const { sasUri } = initResponse.signedUploadUrlDetails;

        this.store.dispatch(
          UploadActions.uploadFileStarted({
            name: fileName,
            uploadId,
          }),
        );

        return this.#uploadFileChunk$(
          { sasUri, uploadId, fileName, chunkSize, parentId: request.parentId },
          file,
        );
      }),
      catchError((error: any) => {
        const errorMsg = error.errorType
          ? `upload.failed.${error.errorType}`
          : 'upload.errors.initFailed';

        return of(
          UploadActions.uploadFileFail({
            name: fileName,
            error: errorMsg,
          }),
        );
      }),
    );
  }

  #uploadFileChunk$(
    { sasUri, uploadId, fileName, chunkSize, parentId }: StorageApiUploadChunkRequest,
    file: File,
    options: Record<string, unknown> = {},
  ): Observable<number> {
    const chunkCount = Math.ceil(file.size / chunkSize);

    return this.uploadService
      .uploadChunks$({
        sasUri,
        file,
        options: {
          parallelUploads: 4,
          maxRetries: 3,
          retryDelay: 1000,
          retryBackoff: 2,
          ...options,
          chunkSize,
          uploadId,
        },
      })
      .pipe(
        tap((progress) => {
          const uploaded = Math.floor((progress / 100) * file.size);
          const remaining = file.size - uploaded;

          this.store.dispatch(
            UploadActions.uploadFileProgress({
              name: fileName,
              progress,
              uploadId,
              uploaded,
              size: file.size,
              speed: 0,
              remaining,
            }),
          );
        }),
        last(),
        switchMap((finalProgress) => {
          if (finalProgress === 100) {
            return of(
              UploadActions.uploadFileComplete({
                name: fileName,
                fileId: uploadId,
                chunkCount,
                parentId,
              }),
            );
          }

          return EMPTY;
        }),
        catchError((error: any) => {
          // if cancellation or empty file - remove silently without error
          const isCancellation = error.message === UPLOAD_ERROR_USER_CANCELLED;
          const isEmptyFile = error.message === UPLOAD_ERROR_EMPTY_FILE;

          if (isCancellation || isEmptyFile) {
            return of(
              UploadActions.clearFileSuccess({
                name: fileName,
              }),
            );
          }

          const errorType = error.errorType || 'UNKNOWN_ERROR';
          const errorMessage = `upload.failed.${errorType}`;

          if ([UploadErrorEnum.FileAlreadyExists].includes(errorType)) {
            return of(
              UploadActions.uploadFileFail({
                name: fileName,
                error: errorMessage,
                params: { name: fileName, status: error.status || 500 },
              }),
            );
          }

          return of(
            UploadActions.clearFile({
              name: fileName,
              fileId: uploadId,
              error: errorMessage,
              params: { name: fileName, status: error.status || 500 },
            }),
          );
        }),
      );
  }

  public uploadFileComplete$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UploadActions.uploadFileComplete),
      mergeMap(({ name, fileId, parentId }) =>
        this.uploadService.completeUpload$(fileId).pipe(
          map((completeResponse) => {
            const fileData = completeResponse.result.fileItem;

            return UploadActions.uploadFileSuccess({ file: fileData, parentId });
          }),
          catchError((error: BackendError) => {
            const errorMsg = error.errorType
              ? `upload.failed.${error.errorType}`
              : 'upload.failed.complete';

            return of(
              UploadActions.uploadFileFail({
                name,
                error: errorMsg,
              }),
            );
          }),
        ),
      ),
    ),
  );

  public clearFile$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UploadActions.clearFile),
      switchMap(({ name, fileId, error }) => {
        const hadInitializedUpload = this.uploadService.cancelActiveUpload(name);
        if (!hadInitializedUpload) return of(UploadActions.clearFileSuccess({ name }));

        return this.uploadService.abortUpload$(fileId, name, Boolean(error)).pipe(
          map(() =>
            error
              ? UploadActions.uploadFileFail({ name, error })
              : UploadActions.clearFileSuccess({ name }),
          ),
          catchError((backendError: BackendError) => {
            const errorKey = backendError.errorType ? backendError.errorType : 'global';
            const parsedError = `upload.failed.${errorKey}`;

            return of(
              UploadActions.clearFileFail({
                error: parsedError,
                name,
              }),
            );
          }),
        );
      }),
    ),
  );

  public uploadFilesFail$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UploadActions.uploadFilesFail),
      switchMap(({ error }) => [ToastActions.addToastError({ message: error })]),
    ),
  );

  private getMimetype(file: File): string {
    const extensions = this.sharedConfigService.get('file');
    const knownMimetypes: string[] = Object.values(extensions);
    const extension = file.name.split('.').pop()?.toLowerCase() || '';

    const isStream = file.type === 'application/octet-stream';
    const mimeType =
      knownMimetypes.find((e) =>
        extension.startsWith(e.split('data-').pop()?.toLowerCase() || ''),
      ) || '';

    const hasKnownExtension = !!mimeType;
    const hasNoMimetype = !file.type && hasKnownExtension;
    const hasWrongMimetype = isStream && hasKnownExtension;

    if ((hasNoMimetype || hasWrongMimetype) && hasKnownExtension) {
      return mimeType;
    }

    return file.type;
  }
}
